import { KeyboardEvent, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { GameHallItem } from "../../api";
import { useCustomColors } from "../../hooks/useCustomColors";
import {
  GameInteraction,
  GameInteractionType,
  PLAYABLE_GAME_ID,
} from "../../utils/games";
import ToastComponent from "../ToastComponent";
import AppDialog from "../lotto/AppDialog";
import FakeGameSignInDialog from "./FakeGameSignInDialog";

export const COMING_SOON_MESSAGE = "即将来临 敬请期待！";

interface GameHallProps {
  games: GameHallItem[];
}

const resolveInteraction = (game: GameHallItem): GameInteractionType =>
  game.interaction ??
  (game.id === PLAYABLE_GAME_ID
    ? GameInteraction.CLICKABLE
    : GameInteraction.DISABLED);

const GameHall = ({ games }: GameHallProps) => {
  const colors = useCustomColors();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [fakeSignInGame, setFakeSignInGame] = useState<string | null>(null);
  const [comingSoonGame, setComingSoonGame] = useState<string | null>(null);

  const activateGame = (game: GameHallItem) => {
    const gameName = game.name || t(game.nameKey || "common_status");

    switch (resolveInteraction(game)) {
      case GameInteraction.CLICKABLE:
        navigate("/game/bet");
        break;
      case GameInteraction.FAKE_SIGN_IN:
        setFakeSignInGame(gameName);
        break;
      case GameInteraction.COMING_SOON:
        setComingSoonGame(gameName);
        break;
      case GameInteraction.DISABLED:
        break;
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    game: GameHallItem
  ) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    activateGame(game);
  };

  return (
    <>
      <Box
        sx={{
          mt: "var(--home-section-gap, 24px)",
          p: { xs: "16px", md: "24px" },
          borderRadius: "20px",
          background: colors.card.gradient,
          border: `1px solid ${colors.border.accent}`,
          boxShadow: colors.card.shadow,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: { xs: "18px", md: "20px" },
            fontWeight: 800,
            fontStyle: "italic",
            color: colors.accent.gold,
          }}
        >
          {t("dashboard_game_hall")}
        </Typography>
        <Typography
          sx={{ mt: "10px", fontSize: "13px", color: colors.text.secondary }}
        >
          {t("dashboard_game_hall_description")}
        </Typography>
        <Box
          sx={{
            mt: "18px",
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: { xs: "10px", md: "16px" },
          }}
        >
          {games.map((game) => {
            const interaction = resolveInteraction(game);
            const isInteractive = interaction !== GameInteraction.DISABLED;
            const accessibleName = game.name || t(game.nameKey || "common_status");

            return (
              <Box
                key={game.code}
                onClick={isInteractive ? () => activateGame(game) : undefined}
                onKeyDown={
                  isInteractive && interaction !== GameInteraction.CLICKABLE
                    ? (event) => handleKeyDown(event, game)
                    : undefined
                }
                role={
                  interaction === GameInteraction.CLICKABLE
                    ? "link"
                    : isInteractive
                      ? "button"
                      : undefined
                }
                tabIndex={
                  isInteractive && interaction !== GameInteraction.CLICKABLE
                    ? 0
                    : undefined
                }
                aria-label={isInteractive ? accessibleName : undefined}
                aria-disabled={!isInteractive}
                sx={{
                  position: "relative",
                  minHeight: { xs: "160px", md: "220px" },
                  borderRadius: "18px",
                  overflow: "hidden",
                  cursor: isInteractive ? "pointer" : "not-allowed",
                  opacity: isInteractive ? 1 : 0.72,
                  border: `1px solid ${colors.border.primary}`,
                  backgroundImage: `linear-gradient(0deg, ${colors.tint.imageScrim} 0%, rgba(0, 0, 0, 0.35) 72%), url(${game.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                  "&:hover": isInteractive
                    ? {
                        borderColor: colors.accent.gold,
                        boxShadow: `${colors.glow.primary}, ${colors.glow.gold}`,
                      }
                    : undefined,
                  "&:focus-visible": isInteractive
                    ? { outline: `2px solid ${colors.accent.gold}`, outlineOffset: "3px" }
                    : undefined,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    p: { xs: "16px", md: "20px" },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                  }}
                >
                  <Box
                    className="primary-shining-pill"
                    sx={{
                      alignSelf: "flex-start",
                      mb: "auto",
                      height: "20px",
                      px: "10px",
                      display: "flex",
                      alignItems: "center",
                      borderRadius: "10px",
                      fontSize: "9px",
                      fontWeight: 800,
                    }}
                  >
                    {game.badge || t(game.badgeKey || "common_status")}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: "13px", md: "16px" },
                      fontWeight: 800,
                      color: colors.text.primary,
                    }}
                  >
                    {accessibleName}
                  </Typography>
                  <Typography
                    sx={{
                      mt: "4px",
                      fontSize: "10px",
                      color: colors.text.secondary,
                    }}
                  >
                    {game.subtitle || t(game.detailKey || "common_status")}
                  </Typography>
                  <Typography
                    sx={{
                      mt: "8px",
                      fontSize: "10px",
                      fontWeight: 800,
                      letterSpacing: "1px",
                      color: colors.accent.gold,
                    }}
                  >
                    {t("dashboard_play_now")} →
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <FakeGameSignInDialog
        open={Boolean(fakeSignInGame)}
        gameName={fakeSignInGame || ""}
        onClose={() => setFakeSignInGame(null)}
      />
      <AppDialog
        open={Boolean(comingSoonGame)}
        onClose={() => setComingSoonGame(null)}
        title={comingSoonGame || ""}
        subtitle="COMING SOON"
      >
        <Typography
          sx={{
            py: "8px",
            textAlign: "center",
            fontSize: { xs: "16px", sm: "18px" },
            fontWeight: 700,
            letterSpacing: ".06em",
            color: "transparent",
            WebkitTextStroke: `1px color-mix(in srgb, ${colors.accent.gold} 65%, transparent)`,
            background: `linear-gradient(0deg, ${colors.accent.goldBright} 0 46%, color-mix(in srgb, ${colors.accent.gold} 55%, transparent) 48%, transparent 52% 100%) 0 0 / 100% 220%`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            animation: "comingSoonFill 3.4s ease-in-out infinite",
            "@keyframes comingSoonFill": {
              "0%, 100%": { backgroundPosition: "0 -110%" },
              "50%": { backgroundPosition: "0 10%" },
            },
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              backgroundPosition: "0 10%",
            },
          }}
        >
          {COMING_SOON_MESSAGE}
        </Typography>
      </AppDialog>
      <ToastComponent autoClose={3000} />
    </>
  );
};

export default GameHall;
